import React, {memo} from 'react'
import {takeRight} from 'lodash'
import {Accordion, Button, Header, Label, List, Tab} from 'semantic-ui-react'
import Logs from './logs'
import {Actions} from '../../../../shared'
import {cloneResource} from '../../../../../backend'
import {
  withChildPathProps,
  withEnvironmentData,
  withErrorHandling,
  withHelmet,
  withPropsPresent,
  withServiceData,
  withVersionData
} from '../../../../../hoc'
import {useSafeState} from '../../../../../hook'
import {getColorByHttpMethod} from '../../../../../util'

const getPathPrefix = (serviceId, versionId, environmentId) => {
  const basePath = '/c/services/:serviceId/versions/:version.id/environments/:environment.id/resources'
  return basePath
    .replace(':serviceId', serviceId)
    .replace(':version.id', versionId)
    .replace(':environment.id', environmentId)
}

const _Resources = (props) => {
  let {
    service,
    version,
    environment,
    resourceId,
    resourceTab = 'responses',
    navigate,
    mutateEnvironment,
    cloneResource,
    children
  } = props

  const [state, setState] = useSafeState({cloning: ''})
  let activeIndex = -1
  const resource = environment.resources.find(r => r.id === resourceId)
  if (resource) {
    activeIndex = environment.resources.indexOf(resource)
  }
  const tabIndex = resourceTab === 'responses' ? 0 : 1

  const onSelect = async (titleProps) => {
    const {index} = titleProps
    const newIndex = activeIndex === index ? -1 : index
    if (newIndex > -1) {
      const resource = environment.resources[newIndex]
      navigate(`${getPathPrefix(service.id, version.id, environment.id)}/${resource.id}/${resourceTab}`)
    } else {
      navigate(getPathPrefix(service.id, version.id, environment.id))
    }
  }

  const onAdd = (e) => {
    e.preventDefault()
    navigate(`${getPathPrefix(service.id, version.id, environment.id)}/add`)
  }

  const onEdit = (resource) => {
    navigate(`${getPathPrefix(service.id, version.id, environment.id)}/${resource.id}/edit`)
  }

  const onRemove = (resource) => {
    navigate(`${getPathPrefix(service.id, version.id, environment.id)}/${resource.id}/remove`)
  }

  const onClone = async (resource) => {
    setState({...state, cloning: resource.id})
    const cloned = await cloneResource(resource.id)
    setState({...state, cloning: ''})
    mutateEnvironment()
    navigate(`${getPathPrefix(service.id, version.id, environment.id)}/${cloned.id}/responses`)
  }

  const onCopyTo = (resource) => {
    navigate(`${getPathPrefix(service.id, version.id, environment.id)}/${resource.id}/copy-to/${service.id}`)
  }

  const onTabSelected = async (tabIndex) => {
    const resourceTab = tabIndex === 0 ? 'responses' : 'logs'
    navigate(`${getPathPrefix(service.id, version.id, environment.id)}/${resource.id}/${resourceTab}`)
  }

  const getContent = (activeIndex, iteratorIndex) => {
    if (activeIndex === iteratorIndex) {
      let panes = [
        {
          menuItem: 'Responses',
          render: () => (
            <Tab.Pane>
              {children}
            </Tab.Pane>
          )
        },
        {
          menuItem: 'Logs',
          render: () => (
            <Tab.Pane>
              {children}
            </Tab.Pane>
          )
        },
      ]
      return (
        <div data-test='resource-content'>
          <div style={{marginBottom: '15px'}}>
            <Tab onTabChange={(e, data) => onTabSelected(data.activeIndex)} panes={panes} activeIndex={tabIndex}/>
          </div>
        </div>
      )
    }
    return null
  }
  const panels = environment.resources.map((res, i) => {
    return {
      key: `${res.id}`,
      title: {
        key: `title-${res.id}`,
        content: (
          <List horizontal style={{display: 'inline'}} data-test='resource-title'>
            <List.Item>
              <Label color={getColorByHttpMethod(res.verb)}
                     data-test='resource-verb'
                     style={{
                       marginRight: '10px',
                       width: '75px',
                       textAlign: 'center'
                     }}>{res.verb.toUpperCase()}</Label>
            </List.Item>
            <List.Item style={{maxWidth: '68%'}}>
              <b style={{whiteSpace: 'pre-wrap', wordWrap: 'break-word'}} data-test='resource-path'>{res.path}</b>
            </List.Item>
            <List.Item style={{float: 'right'}}>
              <Actions
                prefix='resource'
                onEdit={() => onEdit(res)}
                onClone={() => onClone(res)}
                onRemove={() => onRemove(res)}
                onCopyTo={()=> onCopyTo(res)}
                style={{float: 'right'}}
              />
            </List.Item>
          </List>
        )
      },
      content: {
        key: `content-${res.id}`,
        content: getContent(activeIndex, i, res)
      }
    }
  })

  return (
    <div>
      <Header as='h4' dividing>Resources</Header>
      <>
        {
          environment.resources.length > 0 && (
            <Accordion fluid
                       styled
                       activeIndex={activeIndex}
                       panels={panels}
                       data-testid='resources'
                       onTitleClick={(e, titleProps) => onSelect(titleProps)}/>
          )
        }
        {
          environment.resources.length === 0 && (
            <div data-testid='resources'>
              No resources defined
            </div>
          )
        }
        <Button size='tiny'
                basic
                primary
                icon='add'
                content='Add Resource'
                data-testid='resources-add-button'
                style={{marginTop: '25px'}}
                onClick={(e) => onAdd(e)}/>
      </>
      {
        !resource && children
      }
    </div>
  )
}


let Resources = (props) => {
  const {handleError} = props
  const pathname = props.location.pathname
  let resourceTab = takeRight(pathname.split('/'))[0]
  if (!['responses', 'logs', 'remove', 'add'].includes(resourceTab)) {
    resourceTab = 'responses'
  }
  props = {
    ...props,
    resourceTab,
    cloneResource: handleError(cloneResource)
  }
  return (<_Resources {...props}/>)
}

Resources = withPropsPresent(Resources, ['service', 'version', 'environment'])
Resources = withHelmet(Resources, 'Resources')
Resources = withEnvironmentData(Resources, 'Loading environment...')
Resources = withVersionData(Resources, 'Loading version...')
Resources = withServiceData(Resources, 'Loading service...')
Resources = withChildPathProps(Resources)
Resources = withErrorHandling(Resources)
Resources = memo(Resources)

export {_Resources, Resources}

export default Resources